import type { UnknownObject } from '~/types/common'
import type { RouteLocationNormalized } from 'vue-router'

interface BaseContext {
  feature: string
  module: string
  route_name: string | symbol
  route_path: string
  licence_id?: number
  licence_name?: string
  timestamp: number
  [key: string]: unknown
}

interface CrumbData {
  feature: string
  module: string
  route_name: string
  licence_id?: string
  [key: string]: unknown
}

interface UseMonitoringContextReturn {
  getCurrentFeature: () => string
  getCurrentModule: () => string
  getBaseContext: () => BaseContext
  getCrumbData: (extra?: UnknownObject) => CrumbData
}

interface UseMonitoringContextOptions {
  routeOverride?: RouteLocationNormalized
}

export const useMonitoringContext = (options?: UseMonitoringContextOptions): UseMonitoringContextReturn => {
  const authStore = useAuthStore()
  // Use provided route from options (e.g., from middleware) or fallback to useRoute()
  // This prevents "[nuxt] Calling `useRoute` within middleware may lead to misleading results" warning
  const route = options?.routeOverride ?? useRoute()

  const getCurrentFeature = (): string => {
    return (route.meta.feature as string) || 'unknown'
  }

  const getCurrentModule = (): string => {
    return (route.meta.module as string) || 'unknown'
  }

  const getBaseContext = (): BaseContext => {
    return {
      feature: getCurrentFeature(),
      module: getCurrentModule(),
      route_name: route.name ?? 'unknown',
      route_path: route.path ?? 'unknown',
      licence_id: authStore.activeLicence?.id,
      licence_name: authStore.activeLicence?.name,
      timestamp: Date.now()
    }
  }

  const getCrumbData = (extra?: UnknownObject): CrumbData => {
    const base = getBaseContext()
    return {
      feature: base.feature,
      module: base.module,
      route_name: String(base.route_name),
      licence_id: base.licence_id?.toString(),
      ...extra
    }
  }

  return { getCurrentFeature, getCurrentModule, getBaseContext, getCrumbData }
}
